import { useEffect } from 'react';
import SEO from '@/components/SEO';
const BookingPage = () => {
  useEffect(() => {
    // Read URL params
    const qp = new URLSearchParams(window.location.search);

    // Accept several possible token names from Jotform
    const isoParam = qp.get('date') || qp.get('start') || qp.get('datetime') || qp.get('appointment');
    if (isoParam) {
      // Swap to confirmation view
      const bookWrap = document.getElementById('st-book-wrap');
      const confirmDiv = document.getElementById('st-confirm');
      if (bookWrap) {
        bookWrap.style.display = 'none';
        bookWrap.classList.add('hidden');
      }
      if (confirmDiv) {
        confirmDiv.style.display = 'block';
        confirmDiv.classList.remove('hidden');
      }

      // Name -> first name for greeting
      const rawName = (qp.get('name') || '').replace(/\+/g, ' ').trim();
      const firstName = rawName.split(/\s+/)[0] || '';

      // Normalize timezone like "America/Los_Angeles (GMT-07:00)" -> "America/Los_Angeles"
      const rawTz = qp.get('tz') || 'America/Los_Angeles';
      const tz = rawTz.split('(')[0].trim();
      const meet = qp.get('meet') || 'https://meet.google.com/';
      const duration = parseInt((qp.get('durationMinutes') || '30').toString().replace(/\D+/g, ''), 10) || 30;
      const resched = qp.get('resched') || 'https://rebrand.ly/brianthrives';
      const home = qp.get('home') || 'https://seniorthrive.com';
      const whenEl = document.getElementById('stc-when');
      const tzEl = document.getElementById('stc-tz');
      const meetEl = document.getElementById('stc-meet') as HTMLAnchorElement | null;
      const titleEl = document.getElementById('stc-title');
      const gcalBtn = document.getElementById('stc-gcal') as HTMLAnchorElement | null;
      const icsBtn = document.getElementById('stc-ics') as HTMLAnchorElement | null;
      const reschedEl = document.getElementById('stc-resched') as HTMLAnchorElement | null;
      const homeEl = document.getElementById('stc-home') as HTMLAnchorElement | null;
      if (reschedEl) reschedEl.href = resched;
      if (homeEl) homeEl.href = home;
      if (meetEl) meetEl.href = meet;
      const pad = (n: number) => String(n).padStart(2, '0');
      const stamp = (d: Date) => `${d.getUTCFullYear()}${pad(d.getUTCMonth() + 1)}${pad(d.getUTCDate())}T${pad(d.getUTCHours())}${pad(d.getUTCMinutes())}${pad(d.getUTCSeconds())}Z`;
      const fmt = (d: Date, z: string) => new Intl.DateTimeFormat(undefined, {
        weekday: 'short',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
        timeZone: z
      }).format(d);

      // Parse start time: prefer ISO, fallback to "Aug 27, 2025 9:30 AM-10:00 AM"
      let start = new Date(isoParam);
      if (isNaN(start.getTime())) {
        const left = isoParam.split('-')[0];
        if (left) start = new Date(left.trim());
      }
      if (!isNaN(start.getTime())) {
        const end = new Date(start.getTime() + duration * 60000);
        if (whenEl) whenEl.textContent = fmt(start, tz);
        if (tzEl) tzEl.textContent = `Time shown in ${tz}`;
        if (titleEl) titleEl.textContent = firstName ? `You're booked, ${firstName}` : `You're booked`;
        const eventTitle = firstName ? `Thrive Conversation with Brian - ${firstName}` : `Thrive Conversation with Brian`;
        const details = `Join: ${meet}\nAgenda: Thrive Conversation with Brian.`;
        const gcal = `https://calendar.google.com/calendar/render?action=TEMPLATE` + `&text=${encodeURIComponent(eventTitle)}` + `&dates=${stamp(start)}/${stamp(end)}` + `&details=${encodeURIComponent(details)}` + `&ctz=${encodeURIComponent(tz)}`;
        if (gcalBtn) gcalBtn.href = gcal;
        const ics = `BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//SeniorThrive//Thrive Conversation//EN
CALSCALE:GREGORIAN
METHOD:PUBLISH
BEGIN:VEVENT
UID:${Date.now()}@seniorthrive.com
DTSTAMP:${stamp(new Date())}
DTSTART:${stamp(start)}
DTEND:${stamp(end)}
SUMMARY:${eventTitle}
DESCRIPTION:${details.replace(/\n/g, '\\n')}
LOCATION:Online
STATUS:CONFIRMED
END:VEVENT
END:VCALENDAR`;
        const blob = new Blob([ics], {
          type: 'text/calendar;charset=utf-8'
        });
        if (icsBtn) icsBtn.href = URL.createObjectURL(blob);
      }
    }
  }, []);
  return <>
      <SEO title="Book Your Thrive Conversation | SeniorThrive" description="Schedule a personalized conversation with Brian from SeniorThrive. Explore how you can thrive at home with our expert guidance and support." canonical="https://seniorthrive.com/brianthrives" keywords="SeniorThrive booking, thrive conversation, Brian Fluhr, senior care consultation" />

      <div className="bg-gradient-to-br from-background via-background/95 to-primary/5">
        {/* Booking Section */}
        <section id="st-book-wrap" className="py-8">
          <div className="max-w-4xl mx-auto px-4">
            <div className="text-center mb-6">
              <h1 className="text-3xl font-bold text-foreground mb-2">Book a Time to Connect</h1>
              <p className="text-muted-foreground text-lg">Schedule a time to talk about helping older adults thrive confidently at home—with support that works for families and professionals alike.</p>
            </div>

            <div className="bg-card border border-border rounded-2xl shadow-lg overflow-hidden">
              <iframe id="st-book-iframe" className="w-full h-[80vh] min-h-[600px] md:min-h-[700px] border-0" title="SeniorThrive booking form" src="https://form.jotform.com/252376867299073" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" referrerPolicy="strict-origin-when-cross-origin" />
            </div>
          </div>
        </section>

        {/* Confirmation Section */}
        <div id="st-confirm" className="hidden py-10">
          <div className="max-w-4xl mx-auto px-4">
            <div className="bg-card border border-border rounded-2xl shadow-lg p-8">
              <header className="text-center mb-6">
                <h1 id="stc-title" className="text-3xl font-bold text-primary mb-3">You're booked</h1>
                <p className="text-muted-foreground text-lg">
                  Your Thrive Conversation is confirmed. Check your email for the details and calendar invite.
                </p>
              </header>

              <section className="grid md:grid-cols-2 gap-4 mb-6">
                <div className="bg-muted/50 border border-border/50 rounded-xl p-4 text-center">
                  <div className="font-semibold text-foreground mb-2">When</div>
                  <div id="stc-when" className="text-muted-foreground">We emailed your appointment details.</div>
                  <small id="stc-tz" className="text-muted-foreground/80 text-sm"></small>
                </div>
                <div className="bg-muted/50 border border-border/50 rounded-xl p-4 text-center">
                  <div className="font-semibold text-foreground mb-2">Join Link</div>
                  <div>
                    <a id="stc-meet" href="#" target="_blank" rel="noopener" className="text-primary hover:text-primary/80 underline">
                      Opens your meeting
                    </a>
                  </div>
                </div>
              </section>

              <section className="bg-accent/10 border border-accent/20 rounded-xl p-4 mb-6">
                <div className="font-semibold text-accent mb-3">What happens next</div>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <div className="w-3 h-3 rounded-full bg-primary mt-1.5 mr-3 flex-shrink-0"></div>
                    <span className="text-foreground">You'll receive a calendar invite and confirmation email.</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-3 h-3 rounded-full bg-primary mt-1.5 mr-3 flex-shrink-0"></div>
                    <span className="text-foreground">Bring your questions, we'll explore how you can thrive at home.</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-3 h-3 rounded-full bg-primary mt-1.5 mr-3 flex-shrink-0"></div>
                    <span className="text-foreground">If plans change, you can reschedule in one click.</span>
                  </li>
                </ul>
              </section>

              <div className="flex flex-wrap gap-3 justify-center mb-6">
                <a id="stc-gcal" className="inline-flex items-center justify-center min-w-[180px] px-4 py-3 bg-primary text-primary-foreground font-semibold rounded-xl hover:bg-primary/90 transition-colors" href="#" target="_blank" rel="noopener">
                  Add to Google Calendar
                </a>
                <a id="stc-ics" className="inline-flex items-center justify-center min-w-[180px] px-4 py-3 bg-background border border-border text-foreground font-semibold rounded-xl hover:border-border/80 transition-colors" href="#" download="ThriveConversation.ics">
                  Add to Apple or Outlook
                </a>
                <a id="stc-home" className="inline-flex items-center justify-center min-w-[180px] px-4 py-3 bg-background border border-border text-foreground font-semibold rounded-xl hover:border-border/80 transition-colors" href="https://seniorthrive.com">
                  Back to SeniorThrive
                </a>
              </div>

              <p className="text-center text-muted-foreground">
                Need anything before we meet? Email{' '}
                <a href="mailto:brian@seniorthrive.com" className="text-primary hover:text-primary/80 underline">
                  brian@seniorthrive.com
                </a>
                .
                <br />
                <small className="text-muted-foreground/80">Tip: Add a note when you save the event to your calendar.</small>
              </p>
            </div>
          </div>
        </div>
      </div>
    </>;
};
export default BookingPage;